import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext, KeyboardSensor, PointerSensor, closestCenter,
  useSensor, useSensors, type DragEndEvent,
} from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import {
  SortableContext, arrayMove, sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import { api, ApiClientError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useToast } from '../../components/Toast';
import Icon from '../../components/Icon';
import Modal from '../../components/Modal';
import { Banner, EmptyState, PageLoading, TextArea } from '../../components/ui';
import { PageHeader } from '../../pages/AppShell';

import FieldRow from './FieldRow';
import FieldInspector from './FieldInspector';
import PhonePreview from './PhonePreview';
import {
  FIELD_ICON, makeField, sameFields, validateDraft,
} from './fieldMeta';

import { FORM_KINDS, type FormKind } from './formKinds';

import type {
  FieldType, FormField, FormResponse, FormVersion, PublishResult,
} from '../../types';

interface RemovalPrompt {
  removed: string[];
  labels: string[];
  affectedRecords: number;
  message: string;
}

/**
 * One builder, two forms. `kind` carries everything that differs — see
 * formKinds.ts, and formBuilder.service.js for the same move on the
 * server.
 */
export default function FormBuilderPage({ kind = 'customer' }: { kind?: 'customer' | 'visit' | 'lead' }) {
  const cfg: FormKind = FORM_KINDS[kind];
  const { can } = useAuth();
  const toast = useToast();
  const readOnly = !can('company_admin', 'platform_admin');

  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<FormResponse | null>(null);
  const [fields, setFields] = useState<FormField[]>([]);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [changeNote, setChangeNote] = useState('');

  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [removalPrompt, setRemovalPrompt] = useState<RemovalPrompt | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  /** The right rail shows the phone until a field is picked to edit. */
  const [rail, setRail] = useState<'settings' | 'preview'>('preview');
  const [versions, setVersions] = useState<FormVersion[] | null>(null);

  // Detect when the right rail is hidden (responsive breakpoint).
  const [narrow, setNarrow] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 1280px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1280px)');
    const handler = (e: MediaQueryListEvent) => setNarrow(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const closeInspector = useCallback(() => {
    setSelectedKey(null);
    setRail('preview');
  }, []);


  // What the server currently holds, so autosave can skip no-op saves.
  const lastSaved = useRef<FormField[]>([]);
  const saveTimer = useRef<number | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get<FormResponse>(`${cfg.basePath}/form`);
        if (cancelled) return;
        const working = data.draft?.fields ?? data.published.fields;
        setForm(data);
        setFields(working);
        lastSaved.current = data.draft ? working : [];
        setChangeNote(data.draft?.changeNote ?? '');
      } catch (err) {
        if (!cancelled) {
          toast.error(err instanceof ApiClientError ? err.message : 'Could not load the form.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [toast]);

  const publishedKeys = useMemo(
    () => new Set((form?.published.fields ?? []).map((f) => f.key)),
    [form],
  );
  const publishedByKey = useMemo(
    () => new Map((form?.published.fields ?? []).map((f) => [f.key, f])),
    [form],
  );
  const takenKeys = useMemo(() => new Set(fields.map((f) => f.key)), [fields]);
  const errors = useMemo(() => validateDraft(fields), [fields]);
  const hasErrors = Object.keys(errors).length > 0;

  const dirty = useMemo(
    () => !sameFields(fields, form?.published.fields ?? []),
    [fields, form],
  );

  const removedKeys = useMemo(
    () => [...publishedKeys].filter((k) => !takenKeys.has(k)),
    [publishedKeys, takenKeys],
  );

  const selected = fields.find((f) => f.key === selectedKey) ?? null;

  /** Save the draft, debounced. Nothing here touches what reps are using. */
  const scheduleSave = useCallback((next: FormField[]) => {
    if (readOnly) return;
    if (saveTimer.current) window.clearTimeout(saveTimer.current);

    saveTimer.current = window.setTimeout(async () => {
      if (Object.keys(validateDraft(next)).length) return;
      if (sameFields(next, lastSaved.current)) return;

      setSaving(true);
      try {
        await api.put<FormVersion>(`${cfg.basePath}/form/draft`, {
          fields: next.map((f, order) => ({ ...f, order })),
          changeNote: changeNote || undefined,
        });
        lastSaved.current = next;
        setSavedAt(new Date());
      } catch (err) {
        toast.error(err instanceof ApiClientError ? err.message : 'Could not save the draft.');
      } finally {
        setSaving(false);
      }
    }, 900);
  }, [changeNote, readOnly, toast]);

  const update = useCallback((next: FormField[]) => {
    setFields(next);
    scheduleSave(next);
  }, [scheduleSave]);

  useEffect(() => () => {
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
  }, []);

  function addField(type: FieldType) {
    const field = makeField(type, takenKeys);
    update([...fields, field]);
    setSelectedKey(field.key);
    setRail('settings');
  }

  function removeField(key: string) {
    update(fields.filter((f) => f.key !== key));
    if (selectedKey === key) {
      setSelectedKey(null);
      setRail('preview');
    }
  }

  function patchField(next: FormField) {
    const index = fields.findIndex((f) => f.key === selectedKey);
    if (index < 0) return;
    const copy = [...fields];
    copy[index] = next;
    update(copy);
    // The key can change while a field is unpublished.
    if (next.key !== selectedKey) setSelectedKey(next.key);
  }

  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = fields.findIndex((f) => f.key === active.id);
    const to = fields.findIndex((f) => f.key === over.id);
    if (from < 0 || to < 0) return;
    update(arrayMove(fields, from, to));
  }

  async function publish(confirmRemovals = false) {
    if (hasErrors) {
      toast.error('Fix the highlighted fields before publishing.');
      return;
    }

    setPublishing(true);
    try {
      // Flush any pending debounce so we publish what is on screen.
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
      if (!sameFields(fields, lastSaved.current)) {
        await api.put<FormVersion>(`${cfg.basePath}/form/draft`, {
          fields: fields.map((f, order) => ({ ...f, order })),
          changeNote: changeNote || undefined,
        });
        lastSaved.current = fields;
      }

      const result = await api.post<PublishResult>(`${cfg.basePath}/form/publish`, {
        confirmRemovals,
        changeNote: changeNote || undefined,
      });

      setForm((prev) => (prev
        ? { ...prev, published: { ...result, fields: fields.map((f, order) => ({ ...f, order })) }, draft: null }
        : prev));
      lastSaved.current = [];
      setRemovalPrompt(null);
      setRail('preview');
      setChangeNote('');
      setVersions(null);
      setSavedAt(null);

      toast.success(
        result.removed.length
          ? `Version ${result.version} is live. Removed fields keep their data.`
          : `Version ${result.version} is live — reps see it on their next sync.`,
      );
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'CONFIRM_REMOVAL') {
        setRemovalPrompt({
          removed: (err.details?.removed as string[]) ?? [],
          labels: (err.details?.labels as string[]) ?? [],
          // `affectedCustomers` is what the customer form shipped with;
          // `affectedRecords` is the name every form uses now.
          affectedRecords: Number(
            err.details?.affectedRecords ?? err.details?.affectedCustomers ?? 0,
          ),
          message: err.message,
        });
      } else {
        toast.error(err instanceof ApiClientError ? err.message : 'Could not publish.');
      }
    } finally {
      setPublishing(false);
    }
  }

  async function discard() {
    try {
      await api.del(`${cfg.basePath}/form/draft`);
    } catch {
      // No draft on the server is fine — we are resetting the screen either way.
    }
    const published = form?.published.fields ?? [];
    setFields(published);
    lastSaved.current = [];
    setSelectedKey(null);
    setChangeNote('');
    setSavedAt(null);
    toast.toast('Back to the published form.');
  }

  async function openHistory() {
    setHistoryOpen(true);
    if (versions) return;
    try {
      setVersions(await api.get<FormVersion[]>(`${cfg.basePath}/form/versions`));
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not load history.');
    }
  }

  if (loading) {
    return (
      <>
        <PageHeader title={cfg.title} />
        <div className="page"><PageLoading /></div>
      </>
    );
  }

  if (!form) {
    return (
      <>
        <PageHeader title={cfg.title} />
        <div className="page">
          <div className="card">
            <EmptyState
              icon="alert"
              title="The form could not be loaded"
              body="Refresh the page. If it keeps happening, the API may be down."
            />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={cfg.title}
        subtitle={cfg.subtitle(form.published.version)}
        actions={(
          <>
            <button type="button" className="btn btn-ghost" onClick={openHistory}>
              <Icon name="history" size={16} /> History
            </button>
            {dirty && !readOnly ? (
              <button type="button" className="btn btn-secondary" onClick={discard}>
                Discard changes
              </button>
            ) : null}
            <button
              type="button"
              className="btn btn-primary"
              disabled={!dirty || hasErrors || publishing || readOnly}
              onClick={() => publish(false)}
            >
              {publishing ? <span className="spinner" /> : null}
              {dirty ? 'Publish changes' : 'Published'}
            </button>
          </>
        )}
      />

      <div className="page">
        {readOnly ? (
          <div style={{ marginBottom: 16 }}>
            <Banner kind="info">
              You can see the form your company uses, but only a company admin can change it.
            </Banner>
          </div>
        ) : null}

        {dirty && !readOnly ? (
          <div style={{ marginBottom: 16 }}>
            <Banner kind="warning">
              <strong>Draft not published.</strong>{' '}
              Reps are still filling in version {form.published.version}. Nothing changes for
              them until you publish.
              {saving ? ' Saving…' : savedAt ? ` Draft saved ${savedAt.toLocaleTimeString()}.` : ''}
            </Banner>
          </div>
        ) : null}

        <div className="builder">
          {/* -------- palette -------- */}
          <div className="card card-pad">
            <h3 style={{ marginBottom: 4 }}>Add a field</h3>
            <p className="small faint" style={{ marginBottom: 12 }}>
              Tap to add it to the bottom of the form.
            </p>
            {form.fieldTypes.map((type) => (
              <button
                key={type.type}
                type="button"
                className="palette-item"
                data-field-type={type.type}
                disabled={readOnly || fields.length >= cfg.maxFields}
                onClick={() => addField(type.type)}
              >
                <span className="palette-icon">
                  <Icon name={FIELD_ICON[type.type]} size={15} />
                </span>
                <span style={{ minWidth: 0 }}>
                  <span className="palette-name">{type.label}</span>
                  <span className="palette-hint" style={{ display: 'block' }}>{type.hint}</span>
                </span>
              </button>
            ))}
          </div>

          {/* -------- the form -------- */}
          <div className="col gap-16">
            <div className="card">
              <div className="card-head">
                <div>
                  <h3>Form fields</h3>
                  <p className="small faint">Drag to reorder. Click a field to edit it.</p>
                </div>
                <span className="badge badge-neutral">{fields.length} of {cfg.maxFields}</span>
              </div>

              <div className="card-pad">
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                  onDragEnd={onDragEnd}
                >
                  <SortableContext
                    items={fields.map((f) => f.key)}
                    strategy={verticalListSortingStrategy}
                  >
                    {fields.map((field) => (
                      <FieldRow
                        key={field.key}
                        field={field}
                        selected={field.key === selectedKey}
                        error={errors[field.key]}
                        published={publishedKeys.has(field.key)}
                        onSelect={() => { setSelectedKey(field.key); setRail('settings'); }}
                        onRemove={() => removeField(field.key)}
                      />
                    ))}
                  </SortableContext>
                </DndContext>

                {removedKeys.length ? (
                  <div style={{ marginTop: 12 }}>
                    <Banner kind="info">
                      {removedKeys.length === 1 ? 'One field is' : `${removedKeys.length} fields are`}{' '}
                      being removed. Values already captured under{' '}
                      {removedKeys.length === 1 ? 'it' : 'them'} are kept and stay exportable —
                      reps just stop being asked.
                    </Banner>
                  </div>
                ) : null}
              </div>
            </div>

            {dirty && !readOnly ? (
              <div className="card card-pad">
                <TextArea
                  label="What changed (optional)"
                  value={changeNote}
                  maxLength={300}
                  rows={2}
                  placeholder="Added GST number for the distributor audit"
                  hint="Kept with this version so you can see later why the form changed."
                  onChange={(e) => setChangeNote(e.target.value)}
                />
              </div>
            ) : null}
          </div>

          {/*
            One rail, two panels. Stacking them pushed the phone below the
            fold, and the phone is the only place the admin sees what they
            are actually building — a form that looks short as a list is
            obviously long on a 5-inch screen.
          */}
          <div className="builder-preview">
            <div className="inspector col gap-12">
              <div className="tabs" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={rail === 'settings'}
                  className={`tab${rail === 'settings' ? ' active' : ''}`}
                  onClick={() => setRail('settings')}
                >
                  Field settings
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={rail === 'preview'}
                  className={`tab${rail === 'preview' ? ' active' : ''}`}
                  onClick={() => setRail('preview')}
                >
                  Preview
                  <span className="badge badge-neutral">{fields.length}</span>
                </button>
              </div>

              {rail === 'preview' ? (
                <PhonePreview fields={fields} chrome={cfg.preview} />
              ) : selected ? (
                <FieldInspector
                  kind={cfg}
                  field={selected}
                  publishedKeys={publishedKeys}
                  publishedType={publishedByKey.get(selected.key)?.type}
                  takenKeys={takenKeys}
                  onChange={patchField}
                />
              ) : (
                <div className="card">
                  <div className="inspector-empty">
                    Click a field to rename it, mark it required, or change
                    where it appears.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* -------- field inspector modal (narrow screens) -------- */}
      {narrow && selected ? (
        <Modal
          title="Field settings"
          onClose={closeInspector}
          footer={(
            <button
              type="button"
              className="btn btn-secondary"
              onClick={closeInspector}
            >
              Done
            </button>
          )}
        >
          <FieldInspector
            kind={cfg}
            field={selected}
            publishedKeys={publishedKeys}
            publishedType={publishedByKey.get(selected.key)?.type}
            takenKeys={takenKeys}
            onChange={patchField}
          />
        </Modal>
      ) : null}

      {/* -------- removal confirmation -------- */}
      {removalPrompt ? (
        <Modal
          title="Remove these fields from the form?"
          onClose={() => setRemovalPrompt(null)}
          dismissable={false}
          footer={(
            <>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setRemovalPrompt(null)}
              >
                Keep them
              </button>
              <button
                type="button"
                className="btn btn-danger"
                disabled={publishing}
                onClick={() => void publish(true)}
              >
                {publishing ? <span className="spinner" /> : null}
                Remove and publish
              </button>
            </>
          )}
        >
          <div className="col gap-16">
            <div className="col gap-8">
              {removalPrompt.labels.map((label) => (
                <div key={label} className="row gap-8">
                  <Icon name="trash" size={15} />
                  <strong>{label}</strong>
                </div>
              ))}
            </div>

            {removalPrompt.affectedRecords > 0 ? (
              <Banner kind="success" icon={false}>
                <strong>
                  {removalPrompt.affectedRecords.toLocaleString()}{' '}
                  {removalPrompt.affectedRecords === 1 ? cfg.recordNoun : cfg.recordNounPlural}
                  {removalPrompt.affectedRecords === 1 ? ' already has' : ' already have'} a value here.
                </strong>
                <div style={{ marginTop: 4 }}>
                  Nothing is deleted. The values stay on those records and stay in exports —
                  reps simply stop being asked for this going forward.
                </div>
              </Banner>
            ) : (
              <Banner kind="info">
                {`No ${cfg.recordNoun} has a value for this yet, so nothing is affected.`}
              </Banner>
            )}
          </div>
        </Modal>
      ) : null}

      {/* -------- version history -------- */}
      {historyOpen ? (
        <Modal
          title="Form history"
          subtitle="Every published version, newest first"
          onClose={() => setHistoryOpen(false)}
          footer={(
            <button type="button" className="btn btn-secondary" onClick={() => setHistoryOpen(false)}>
              Close
            </button>
          )}
        >
          {!versions ? <PageLoading rows={3} /> : versions.length === 0 ? (
            <EmptyState title="No versions yet" />
          ) : (
            <div>
              {versions.map((version) => (
                <div
                  key={version.id}
                  className={`version-row${version.version === form.published.version ? ' current' : ''}`}
                >
                  <div className="version-no">v{version.version}</div>
                  <div className="grow">
                    <div className="row gap-8">
                      <strong>{version.fields.length} fields</strong>
                      {version.version === form.published.version ? (
                        <span className="badge badge-success">Live</span>
                      ) : null}
                    </div>
                    <div className="small faint">
                      {version.publishedAt
                        ? new Date(version.publishedAt).toLocaleString()
                        : '—'}
                      {version.publishedBy ? ` · ${version.publishedBy}` : ''}
                    </div>
                    {version.changeNote ? (
                      <div className="small muted" style={{ marginTop: 4 }}>
                        {version.changeNote}
                      </div>
                    ) : null}
                    <div className="small faint" style={{ marginTop: 4 }}>
                      {version.fields.map((f) => f.label).join(' · ')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Modal>
      ) : null}
    </>
  );
}
